import { Box, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import React from "react"

import type { CompanyDepartment, ContactQueryableFields } from "@/graphql/codegen/graphql"
import { TypeChips } from "@/screens/Companies/components/filters/company/TypeChip"
import { StatusChip } from "@/screens/Companies/components/filters/location/StatusChip"
import type { TreeNode } from "@/types/tree"

export const LocationSearchFilterChips: React.FC<{ companyTypes: TreeNode<string>[] }> = ({ companyTypes }) => {
  const navigate = useNavigate({ from: "/app/companies" })
  const { isLocationActive, types } = useSearch({
    from: "/app/companies/",
    strict: true,
  })

  const handleRemoveFilter = (
    key: keyof ContactQueryableFields | "types",
    value: string | CompanyDepartment | boolean
  ) => {
    void navigate({
      search: (prev: Record<string, unknown>) => {
        const { isLocationActive, ...newFilter } = prev
        if (Array.isArray(newFilter[key])) {
          newFilter[key] = (newFilter[key] as (string | CompanyDepartment | boolean)[]).filter((item) => item !== value)
          if ((newFilter[key] as (string | CompanyDepartment | boolean)[]).length === 0) {
            delete newFilter[key] // Remove the filter entirely if it's empty
          }
        } else {
          delete newFilter[key]
        }
        return newFilter
      },
      replace: true,
    })
  }

  if (isLocationActive === undefined && !types) {
    return null
  }

  return (
    <Box data-testid='location-search-filter-chips' display='flex' flexWrap='wrap' alignItems='center' gap={2}>
      <Typography variant='body1'>Filters:</Typography>
      <StatusChip
        isActive={!!isLocationActive}
        onRemove={(_, value: boolean) => handleRemoveFilter("isActive", value)}
      />
      <TypeChips
        companyTypes={companyTypes}
        selectedTypes={types || []}
        onRemove={(key, value) => handleRemoveFilter("types", value)}
      />
    </Box>
  )
}
